import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useDocumentOptions, useFolderOptions } from "../../../hooks/useMenuOptions";
import { OptionProps } from "./SortingTableMenu";
import { ExamOverviewRow } from "../../../interfaces/examInterfaces";
import { Autocomplete, InputBase } from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import { useEffect, useState } from "react";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import ArticleIcon from "@mui/icons-material/Article";
import { useNavigate } from "react-router-dom";

interface EnhancedTableToolbarProps {
  paths: { [key: string]: ExamOverviewRow[] } | null;
  rowSelected: ExamOverviewRow | null;
  openExam: (examId: string) => void;
  validGrades: number[] | null;
}

interface SearchOption {
  id: string;
  name: string;
  path: string;
  grade: number | undefined;
  isDirectory: boolean;
}

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: "12ch",
  transition: theme.transitions.create("width"),
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    "&:focus-within": {
      width: "30ch", // Expands when input is focused
    },
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    [theme.breakpoints.up("sm")]: {
      width: "12ch",
    },
  },
}));

export function EnhancedTableToolbar({ paths, rowSelected, openExam, validGrades }: EnhancedTableToolbarProps) {
  const navigate = useNavigate();
  const documentOptions = useDocumentOptions({ openExam });
  const folderOptions = useFolderOptions();
  const options = rowSelected?.isDirectory ? folderOptions : documentOptions;
  const [inputValueSearch, setInputValueSearch] = useState("");
  const [searchDropDownOpen, setSearchDropDownOpen] = useState(false);
  const [searchOptions, setSearchOptions] = useState<SearchOption[]>([]);
  const [accessExpired, setAccessExired] = useState<Boolean | undefined>(undefined);

  useEffect(() => {
    createSearchOptions();
  }, []);

  useEffect(() => {
    checkAccessExpired();
  }, [rowSelected]);

  function checkAccessExpired() {
    if (rowSelected && rowSelected.grade) {
      const accessExpired = !validGrades?.includes(rowSelected.grade);
      setAccessExired(accessExpired);
    }
  }

  function createSearchOptions() {
    let formattedPaths: SearchOption[] = [];
    if (paths) {
      const entries = Object.entries(paths);
      for (const [pathKey, optionsArray] of entries) {
        for (const option of optionsArray) {
          const newOption: SearchOption = {
            id: option.id,
            name: option.name,
            path: pathKey,
            grade: option.grade,
            isDirectory: option.isDirectory ?? false,
          };
          formattedPaths.push(newOption);
        }
      }
      setSearchOptions(formattedPaths);
    }
  }

  function closeSearchBar() {
    setSearchDropDownOpen(false);
    setInputValueSearch("");
  }

  function navigateToTarget(rowData: SearchOption) {
    let updatedFolderPath = rowData.path;
    let selectedRow: ExamOverviewRow | null = {
      id: rowData.id,
      name: rowData.name,
      showOptions: true,
      grade: rowData.grade,
    };

    if (rowData.isDirectory) {
      updatedFolderPath = updatedFolderPath + rowData.name;
      selectedRow = null;
    }

    closeSearchBar();
    navigate("/exam-overview" + updatedFolderPath, { state: { selectedRow } });
  }

  return (
    <Toolbar
      sx={[
        {
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 },
          justifyContent: "flex-end",
        },
      ]}
    >
      {rowSelected ? (
        <Typography sx={{ flex: "1 1" }} color="inherit" variant="subtitle1" component="div">
          Aktionen
        </Typography>
      ) : null}
      {rowSelected ? (
        <Box>
          <Options options={options} accessExpired={accessExpired} />
        </Box>
      ) : (
        <Box>
          <Search>
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <Autocomplete
              freeSolo
              disableClearable
              options={searchOptions}
              getOptionLabel={(option) => (typeof option === "string" ? option : option.name)}
              filterOptions={(options, state) =>
                options.filter((option) => option.name.toLowerCase().includes(state.inputValue.toLowerCase()))
              }
              open={searchDropDownOpen}
              onOpen={() => {
                if (inputValueSearch.trim() !== "") {
                  setSearchDropDownOpen(true);
                }
              }}
              onClose={() => closeSearchBar()}
              inputValue={inputValueSearch}
              onInputChange={(event, value) => {
                setInputValueSearch(value);
                if (value.trim() === "") {
                  setSearchDropDownOpen(false);
                } else {
                  setSearchDropDownOpen(true);
                }
              }}
              renderOption={(props, option) => (
                <li
                  {...props}
                  key={option.id}
                  style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}
                  onClick={() => navigateToTarget(option)}
                >
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {option.isDirectory ? (
                      <FolderOpenIcon sx={{ fontSize: 20, mr: 1 }} />
                    ) : (
                      <ArticleIcon sx={{ fontSize: 20, mr: 1 }} />
                    )}
                    <Typography variant="body1">{option.name}</Typography>
                  </Box>
                  {!option.isDirectory && (
                    <Typography variant="caption" sx={{ pl: 4, color: "gray", fontSize: "1.2rem" }}>
                      {option.grade}. Klasse
                    </Typography>
                  )}

                  <Typography variant="caption" sx={{ pl: 4, color: "gray", fontSize: "1.2rem" }}>
                    Ordner: {option.path}
                  </Typography>
                </li>
              )}
              renderInput={(params) => (
                <StyledInputBase
                  {...params.InputProps}
                  inputProps={{
                    ...params.inputProps,
                    "aria-label": "search",
                  }}
                  fullWidth
                />
              )}
            />
          </Search>
        </Box>
      )}
    </Toolbar>
  );
}

function Options({ options, accessExpired }: { options: OptionProps[]; accessExpired: Boolean | undefined }) {
  return (
    <>
      {options.map((option, index) => (
        <Tooltip title={option.title} key={index}>
          <IconButton
            onClick={() => {
              if (!accessExpired) {
                option.action!();
              } else if (accessExpired && option.activeWhenNoValidLicense) {
                option.action!();
              }
            }}
            sx={{
              opacity: !accessExpired || option.activeWhenNoValidLicense ? 1 : 0.5,
            }}
          >
            {option.icon}
          </IconButton>
        </Tooltip>
      ))}
    </>
  );
}

export default EnhancedTableToolbar;
